import { http } from './http';

export type RouteWaypoint = {
  lat: number;
  lon: number;
  alt?: number;
  acc?: number;
  bat?: number;
  spd?: number;
  odo?: number;
  time?: string | Date;
};

export type RouteCreator = {
  name?: string;
  username?: string;
  photo?: { medium?: string; large?: string } | null;
};

export type RouteDestination = {
  address: string;
  name?: string | null;
  lat: number;
  lng: number;
  placeId?: string | null;
  setAt?: string | Date;
};

/**
 * Transportation subdoc per arrivesafe-api PR #149 (schema + socket event)
 * and #151 (currentLat/currentLng on updates). The extra fields are optional
 * because routes written before #151 lack them, and the `route` socket
 * event's transportation projection carries `currentType` only.
 */
export type RouteTransportation = {
  currentType:
    | 'foot'
    | 'running'
    | 'bike'
    | 'car'
    | 'motorcycle'
    | 'bus'
    | 'train'
    | 'plane'
    | 'boat'
    | 'horse'
    | 'detecting';
  currentSource?: 'auto' | 'manual';
  currentConfidence?: number;
  currentLat?: number;
  currentLng?: number;
  updatedAt?: string;
};

/**
 * Emergency subdoc per arrivesafe-api PR #141 (closes part of #134).
 *
 * Shape matches the server-side `RouteEmergency` interface in
 * `src/helper/apiFormat.ts` of the API repo. `triggeredBy.userID` is sent
 * over the wire — we don't render it, but it's in the type so a future
 * privacy review (or a Sentry filter) can see it's exposed.
 *
 * PR #143 shipped the matching `emergency` socket event (handled in
 * socket.ts). `alertedCount` (#180) is only present on REST reads — the
 * socket payload never carries it, so merges must preserve it.
 */
export type RouteEmergency = {
  state: 'active' | 'cleared';
  triggeredAt: string;
  clearedAt?: string;
  location: { lat: number; lng: number; address?: string };
  triggeredBy: { userID: string; name?: string };
  alertedCount?: number;
};

/**
 * Journey event per arrivesafe-api PR #180. `type` is a string on the wire:
 * routes written before #180 can carry legacy free-form types, so consumers
 * narrow with `isRouteEventType` (lib/journeyEvents.ts) at render time.
 * Known types: route_started, battery_drop, stationary, companion_joined,
 * still_en_route, arrived, emergency. Field presence varies by type.
 */
export type RouteEvent = {
  type: string;
  time?: string | Date;
  lat?: number;
  lon?: number;
  acc?: number;
  address?: string;
  battery?: number;
  batteryDelta?: number;
  speed?: number;
  progress?: number;
  durationSeconds?: number;
  companionCount?: number;
  userID?: string;
  name?: string;
};

export type Route = {
  id: string;
  title?: string | null;
  creator: RouteCreator;
  created_at: string;
  finished: boolean;
  finished_at?: string | null;
  address_start?: string | null;
  address_arrival?: string | null;
  waypoints: RouteWaypoint[];
  distance?: number;
  destination?: RouteDestination | null;
  transportation?: RouteTransportation | null;
  emergency?: RouteEmergency | null;
  /** Journey event log per #180; empty/absent on routes from before it shipped. */
  events?: RouteEvent[];
  /** Seconds, per #180. Elapsed-so-far on live routes, created→finished once done. */
  duration?: number;
  /** km/h with one decimal, per #180. */
  average_speed?: number;
  /**
   * Set on emergency-from-idle stub routes (#143). route-show does not
   * currently surface it, so never gate behavior on this flag — key off
   * `emergency` instead. Typed tolerantly in case the API starts sending it.
   */
  emergency_only?: boolean;
  share_code?: string;
};

type VerifyResponse = {
  success?: boolean;
  route_id?: string;
  message?: string;
};

export async function verifyShareCode(shareCode: string): Promise<string> {
  const response = await http<VerifyResponse>('/route/verify', {
    method: 'POST',
    body: { share_code: shareCode },
    auth: true,
  });
  if (!response.route_id) {
    throw new Error(response.message ?? 'verify failed');
  }
  return response.route_id;
}

export async function fetchRoute(routeId: string): Promise<Route> {
  return http<Route>(`/route/${routeId}`, {
    method: 'GET',
    auth: true,
  });
}
